import type { PlayableRunState, Snapshot } from '@dissidium/contracts';
import { closeRun } from './eligibility.ts';
import { bindLiving, demanifest, transferItem, promote } from './ledger.ts';
import { makeEnvelope } from '../seed/politics.ts';
import { initializeLife, reconcilePlayableState, validatePlayableState, type LifeInitializer, type LifeOptions } from '../kernel/playable.ts';

/** The endpoint is actual play state. Relinquish the bequest before promotion takes its audit. */
export function closePlayableLife(input: PlayableRunState, bequestId: string | null = null): PlayableRunState {
  const state = reconcilePlayableState(input);
  if (state.closure) throw new Error('This life was already closed');
  const pc = state.continuity.persons[state.pcId]!;
  const outcome = pc.lifecycle === 'dead' ? 'died' : 'survived';
  if (outcome === 'survived' && (!state.campaign.outcomeId || !state.quests.facts.includes('departure:still-place'))) {
    throw new Error('Finish the ordinary outcome and reach the departure refuge before closing a surviving life');
  }
  // Authored departure scenes record the companion's own shelter decision.
  // Being somewhere in Z04 alone is not evidence of entering its still place.
  for (const fact of state.quests.facts.filter(f => f.startsWith('departure:sheltered:'))) {
    const id = fact.slice('departure:sheltered:'.length);
    const companion = state.continuity.persons[id];
    if (!state.companions[id] || !companion) throw new Error('A sheltered companion needs their own existing contract and identity');
    if (companion.lifecycle === 'dead') continue;
    if (companion.lifecycle !== 'active') throw new Error('A departure needs a physically present companion');
    state.continuity = promote(state.continuity, id, 'supporting');
  }
  const runId = `run:${state.envelope.runOrdinal}`;
  state.continuity = closeRun(state.continuity, {
    id: runId, chronologyId: state.envelope.chronologyId, ordinal: state.envelope.runOrdinal,
    namespace: state.envelope.namespace, rulesetId: state.envelope.rulesetId,
    outcome, pcId: state.pcId, actions: state.verifications,
  }, state.observations, bequestId);
  for (const evidence of state.evidence.filter(e => e.verified)) {
    state.continuity.echoes.push({
      id: `record:${runId}:${evidence.id}`, runId,
      immutableCore: JSON.stringify({ evidenceId: evidence.id, sourceId: evidence.sourceId, finding: evidence.finding, proofId: evidence.proofId }),
      source: { document: 'docs/world/07-cycle.md', section: 'The ordinary half-remembering' },
    });
  }
  state.closure = { outcome, bequestId };
  state.participation = 'ended';
  state.paused = true;
  state.dialogue = null;
  for (const scene of Object.values(state.world.scenes)) { scene.combat.paused = true; scene.combat.pcQueue = []; }
  state.journal.push({
    id: `closure:${runId}`, tick: state.tick, kind: 'closure', sourceId: state.pcId,
    text: outcome === 'survived' ? `${pc.current.name} reached the refuge alive. Their possessions and promises remain theirs.` : `${pc.current.name}'s life ended. The remaining interval passes; the Return arrives in its own time.`,
  });
  return reconcilePlayableState(state);
}

/** Compact second-life route: reserve a reachable former PC, without changing their endpoint. */
export function nextPlayableLife(input: PlayableRunState, options: LifeOptions, initialize: LifeInitializer): PlayableRunState {
  const closed = validatePlayableState(input);
  if (!closed.closure) throw new Error('Close this life before starting the next');
  const frozen = closed.continuity;
  const envelope = { ...makeEnvelope(frozen, options.seed, closed.envelope.contentManifestHash), originId: options.originId };
  const state = initializeLife(envelope, frozen, run => {
    const latest = [...run.continuity.closedRuns].reverse().find(r => r.outcome === 'survived' && run.continuity.persons[r.pcId]?.lifecycle !== 'dead');
    // The slice places one former PC: the most recent survivor, back at the still place even if they walked out
    // with the last outsider. Older survivors stay alive, unmanifested, elsewhere.
    for (const id of run.continuity.legacyIds) if (run.continuity.persons[id]?.lifecycle === 'active') run.continuity = demanifest(run.continuity, id);
    if (latest && run.continuity.persons[latest.pcId]?.lifecycle === 'unmanifested') run.continuity = bindLiving(run.continuity, latest.pcId, 'legacy', 'Z04');
    if (run.continuity.pendingBequest) {
      const id = run.continuity.pendingBequest;
      run.continuity = transferItem(run.continuity, id, { kind: 'bequest', id: run.continuity.chronologyId }, { kind: 'person', id: run.pcId });
    }
    return initialize(run);
  });
  if (frozen.pendingBequest) state.journal.push({ id: `bequest:${envelope.runOrdinal}`, tick: 0, kind: 'event', text: `An unclaimed parcel contains ${state.continuity.items[frozen.pendingBequest]!.name}.`, sourceId: frozen.pendingBequest });
  return validatePlayableState(state);
}

/** A presentation of current authority, never the original promotion audit as a respawn kit. */
export function legacyEncounter(input: PlayableRunState, personId: string): Readonly<{ personId: string; current: Snapshot; demand: string; canBypass: true }> {
  const state = validatePlayableState(input);
  const person = state.continuity.persons[personId];
  const pc = state.continuity.persons[state.pcId]!;
  if (!state.continuity.legacyIds.includes(personId) || !person?.original || person.lifecycle !== 'active' || person.location !== pc.location) throw new Error('This living survivor is not here to meet');
  return {
    personId, current: structuredClone(person.current),
    demand: person.current.oaths[0] ?? person.current.relationships[0]?.fact ?? 'Acknowledge the life I lived; do not claim my possessions or service.',
    canBypass: true,
  };
}

export function meetLegacy(input: PlayableRunState, personId: string): PlayableRunState {
  const encounter = legacyEncounter(input, personId);
  const state = structuredClone(input);
  if (state.participation !== 'active') throw new Error('A dead PC cannot meet a survivor');
  const id = `meeting:${personId}`;
  if (state.verifications.some(a => a.id === id)) throw new Error('This meeting was already recorded');
  state.verifications.push({ id, proofId: 'P-I1', routeId: 'legacy:direct-meeting', method: 'direct-legacy', witnessId: personId });
  state.observations.push({ id: `observation:${state.envelope.runOrdinal}:${id}`, proofId: 'P-I1', runId: `run:${state.envelope.runOrdinal}`, actionId: id, routeId: 'legacy:direct-meeting' });
  state.quests.facts = [...new Set([...state.quests.facts, 'knowledge:continuing-survivor', 'access:comparison-book'])];
  state.journal.push({ id, tick: state.tick, kind: 'legacy', sourceId: personId, text: `${encounter.current.name} remembers a different arrangement. ${encounter.demand}` });
  return validatePlayableState(state);
}

/** Deposited records carry knowledge; their reading grants access, never a new verification or skill. */
export function readContinuityRecord(input: PlayableRunState, recordId: string): PlayableRunState {
  const state = validatePlayableState(input);
  if (state.participation !== 'active' || state.continuity.persons[state.pcId]!.location !== 'Z03') throw new Error('Read deposited records at the public landing');
  const record = state.frozenContinuity.echoes.find(e => e.id === recordId && e.id.startsWith('record:'));
  if (!record) throw new Error('No such deposited record belongs to this opening');
  const id = `read:${record.id}`;
  if (state.journal.some(e => e.id === id)) throw new Error('This record was already read');
  const evidence: unknown = JSON.parse(record.immutableCore);
  if (!evidence || typeof evidence !== 'object' || !('finding' in evidence) || typeof evidence.finding !== 'string' || !('sourceId' in evidence) || typeof evidence.sourceId !== 'string') throw new Error('The deposited evidence record is unreadable');
  state.quests.facts = [...new Set([...state.quests.facts, `knowledge:${evidence.sourceId}`, 'access:comparison-book'])];
  state.journal.push({ id, tick: state.tick, kind: 'evidence', sourceId: record.id, text: evidence.finding });
  return state;
}
